import React from 'react';

// Compute the unified range stats shared by the Index / VIX / BTC cards.
//   slice    : bars already filtered to the selected date range (sorted asc)
//   valueKey : 'close' | 'price' | 'value'
//   current  : the card's live current value (data.current / data.current.value)
// Returns null when the slice is empty.
export function rangeStats(slice, valueKey, current) {
  if (!slice.length) return null;
  let high = slice[0];
  let low = slice[0];
  for (const b of slice) {
    if (b[valueKey] > high[valueKey]) high = b;
    if (b[valueKey] < low[valueKey]) low = b;
  }
  const open = slice[0][valueKey];
  const close = slice[slice.length - 1][valueKey];
  const cur = current ?? close;
  const daysSince = (iso) => {
    if (!iso) return null;
    return Math.max(0, Math.round((Date.now() - new Date(iso)) / 86400000));
  };
  const sorted = slice.map((b) => b[valueKey]).sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  const median = sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
  return {
    open,
    close,
    current: cur,
    count: slice.length,
    high: high[valueKey],
    highDate: high.date,
    low: low[valueKey],
    lowDate: low.date,
    median,
    changePct: open ? ((close - open) / open) * 100 : null,
    pctFromHigh: high[valueKey] ? ((cur - high[valueKey]) / high[valueKey]) * 100 : null,
    pctFromLow: low[valueKey] ? ((cur - low[valueKey]) / low[valueKey]) * 100 : null,
    daysSinceHigh: daysSince(high.date),
    daysSinceLow: daysSince(low.date),
  };
}

export const fmtPct = (v) => (v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`);
export const pctColor = (v) => (v == null ? 'text-gray-300' : v >= 0 ? 'text-green-400' : 'text-red-400');

// Renders the unified stat block. `fmtValue` formats the numeric value
// (USD for price cards, plain number for VIX). `showMedian` inserts a
// "Range median" row after "Range low" (used by the VIX card).
export function StatRows({ stats, fmtValue, showMedian = false }) {
  if (!stats) {
    return <div className="text-xs text-gray-500 mb-3">No bars in selected range</div>;
  }
  return (
    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs mb-3">
      <div className="text-gray-400">Range high</div>
      <div className="text-right">
        <span className="text-gray-200">{fmtValue(stats.high)}</span>{' '}
        <span className="text-gray-500">{stats.highDate}</span>
      </div>
      <div className="text-gray-400">Range low</div>
      <div className="text-right">
        <span className="text-gray-200">{fmtValue(stats.low)}</span>{' '}
        <span className="text-gray-500">{stats.lowDate}</span>
      </div>
      {showMedian && (
        <>
          <div className="text-gray-400">Range median</div>
          <div className="text-right">
            <span className="text-gray-200">{fmtValue(stats.median)}</span>
          </div>
        </>
      )}
      <div className="text-gray-400">% from high</div>
      <div className={`text-right ${pctColor(stats.pctFromHigh)}`}>{fmtPct(stats.pctFromHigh)}</div>
      <div className="text-gray-400">% from low</div>
      <div className={`text-right ${pctColor(stats.pctFromLow)}`}>{fmtPct(stats.pctFromLow)}</div>
      <div className="text-gray-400">Days since high</div>
      <div className="text-right text-gray-200">{stats.daysSinceHigh ?? '—'}</div>
      <div className="text-gray-400">Days since low</div>
      <div className="text-right text-gray-200">{stats.daysSinceLow ?? '—'}</div>
    </div>
  );
}
